import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, StatusBadge, toneForStatus } from '@ibe/ui';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { type ColumnDescriptor } from '../../../lib/resource-list/columns.js';
import { type FilterDescriptor } from '../../../lib/resource-list/filters.js';
import { cancellationPolicyEndpoints } from '../api/endpoints.js';
import { useSoftDeleteCancellationPolicy } from '../api/mutations.js';
import {
  POLICY_STATUS_OPTIONS,
  type CancellationPolicyRow,
  cancellationPolicyRow,
} from '../api/schemas.js';

/**
 * The cancellation policies list.
 *
 * Search, sort, pagination, filtering and the three non-data states all come
 * from `ResourceListPage`. What is here is the descriptor: which columns
 * exist, which backend fields each one reads, and what a row can be done to.
 *
 * `contentHtml` is not a column and must not become one. A table cell cannot
 * show a document, and projecting it would ship up to 64 kB per row to render
 * nothing.
 */

const filters: readonly FilterDescriptor[] = [
  {
    id: 'propertyId',
    label: 'Property',
    param: 'propertyId',
    kind: 'entity',
    optionsPath: '/v1/properties/options',
  },
  {
    id: 'status',
    label: 'Status',
    param: 'status',
    kind: 'select',
    options: POLICY_STATUS_OPTIONS,
  },
];

/** A UTC calendar date. The value is an ISO instant; the day is what reads. */
function formatDate(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export const cancellationPolicyColumns: readonly ColumnDescriptor<CancellationPolicyRow>[] = [
  {
    id: 'name',
    header: 'Policy',
    sortable: true,
    alwaysVisible: true,
    fields: ['name', 'code'],
    cell: (row) => (
      <PrimaryCell
        label={row.name ?? row.id}
        to={`/cancellation-policies/${row.id}`}
        secondary={row.code ?? null}
      />
    ),
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    fields: ['status'],
    cell: (row) =>
      row.status === undefined ? null : (
        <StatusBadge tone={toneForStatus(row.status)}>{row.status}</StatusBadge>
      ),
  },
  {
    id: 'updatedAt',
    header: 'Updated',
    sortable: true,
    fields: ['updatedAt'],
    cell: (row) => formatDate(row.updatedAt),
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['name'],
    cell: (row: CancellationPolicyRow): React.JSX.Element => (
      <CancellationPolicyRowActions row={row} />
    ),
  },
];

/**
 * The per-row controls, as a component rather than inline markup.
 *
 * Deleting needs a mutation, a confirmation and a permission check, and a
 * column descriptor's `cell` is a plain function — it cannot hold hooks. This
 * is the smallest thing that can.
 */
function CancellationPolicyRowActions({
  row,
}: {
  readonly row: CancellationPolicyRow;
}): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = useSoftDeleteCancellationPolicy();
  const label = row.name ?? row.id;

  return (
    <>
      <div className="flex flex-nowrap items-center justify-end gap-1">
        <RowActions
          actions={[
            { label: `View ${label}`, icon: Eye, to: `/cancellation-policies/${row.id}` },
            { label: `Edit ${label}`, icon: Pencil, to: `/cancellation-policies/${row.id}/edit` },
          ]}
        />
        <PermissionGuard permission={PERMISSIONS.CANCELLATION_POLICY_DELETE}>
          <RowActions
            actions={[
              {
                label: `Delete ${label}`,
                icon: Trash2,
                destructive: true,
                onClick: () => {
                  setConfirming(true);
                },
              },
            ]}
          />
        </PermissionGuard>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete cancellation policy"
        description={`${label} will be removed from this list and offered to no new rate plan. Rate plans that already use it keep it, and the policy can be restored.`}
        confirmLabel="Delete"
        destructive
        loading={softDelete.isPending}
        onConfirm={() => {
          softDelete.mutate(row.id, {
            onSuccess: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}

export function CancellationPolicyListPage(): React.JSX.Element {
  const { search } = useLocation();

  return (
    <ResourceListPage
      title="Cancellation policies"
      description="The terms a guest agrees to when they book a rate that carries this policy."
      path={cancellationPolicyEndpoints.list}
      resource="cancellationPolicies"
      schema={cancellationPolicyRow}
      columns={cancellationPolicyColumns}
      filters={filters}
      rowKey={(row) => row.id}
      permission={PERMISSIONS.CANCELLATION_POLICY_READ}
      actions={
        <PermissionGuard permission={PERMISSIONS.CANCELLATION_POLICY_CREATE}>
          <Button asChild>
            <Link to={{ pathname: '/cancellation-policies/new', search }}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              New cancellation policy
            </Link>
          </Button>
        </PermissionGuard>
      }
    />
  );
}
